import { TRACO } from '@/shared/formatters';
import type { PeriodoPresetKey, PeriodoValor } from '@/shared/types/filtroPeriodo';
import { flexionar } from '@/shared/utils/text';
import {
  deIsoData,
  paraIsoData,
  intervaloDoPreset,
  intervaloOrdenado,
  isoParaBr,
  PERIODO_PRESETS,
  PERIODO_VALOR_VAZIO,
  periodoEstaAtivo,
  rotuloDoIntervalo,
  rotuloDoPreset,
} from '@/shared/utils/periodoFiltro';

import type { NegociacaoPeriodoCampo, NegociacaoPeriodoParams } from '../types';
import type { NegociacaoListRow } from '../types/negociacoesUiTypes';
import { periodoCampoOptions } from './negociacoesFilterOptions';

const PARAM_CAMPO = 'periodo';
const PARAM_INICIO = 'de';
const PARAM_FIM = 'ate';
const PARAM_ATALHO = 'atalho';
const CAMPO_PADRAO: NegociacaoPeriodoCampo = 'criacao';

export const PERIODO_NEGOCIACAO_VAZIO: PeriodoValor = {
  ...PERIODO_VALOR_VAZIO,
  campo: CAMPO_PADRAO,
};

const ehCampo = (valor: string | null): valor is NegociacaoPeriodoCampo =>
  periodoCampoOptions.some((opcao) => opcao.key === valor);

const ehAtalho = (valor: string | null): valor is PeriodoPresetKey =>
  PERIODO_PRESETS.some((preset) => preset.key === valor);

const atalhoAindaVale = (atalho: PeriodoPresetKey, inicio: string, fim: string, hoje: Date) => {
  const intervalo = intervaloDoPreset(atalho, hoje);
  return intervalo.inicio === inicio && intervalo.fim === fim;
};

const normalizar = (valor: string | null): string | null => {
  const data = deIsoData(valor);
  return data ? paraIsoData(data) : null;
};

export function lerPeriodoDaUrl(params: URLSearchParams, hoje: Date = new Date()): PeriodoValor {
  const intervalo = intervaloOrdenado({
    inicio: normalizar(params.get(PARAM_INICIO)),
    fim: normalizar(params.get(PARAM_FIM)),
  });
  if (!intervalo) return PERIODO_NEGOCIACAO_VAZIO;
  const [inicio, fim] = intervalo;
  const campo = params.get(PARAM_CAMPO);
  const atalho = params.get(PARAM_ATALHO);
  return {
    campo: ehCampo(campo) ? campo : CAMPO_PADRAO,
    inicio,
    fim,
    preset: ehAtalho(atalho) && atalhoAindaVale(atalho, inicio, fim, hoje) ? atalho : 'custom',
  };
}

export function escreverPeriodoNaUrl(
  params: URLSearchParams,
  valor: PeriodoValor,
): URLSearchParams {
  const proximo = new URLSearchParams(params);
  [PARAM_CAMPO, PARAM_INICIO, PARAM_FIM, PARAM_ATALHO].forEach((chave) => proximo.delete(chave));
  const intervalo = intervaloOrdenado(valor);
  if (!intervalo) return proximo;
  const [inicio, fim] = intervalo;
  proximo.set(PARAM_CAMPO, ehCampo(valor.campo) ? valor.campo : CAMPO_PADRAO);
  proximo.set(PARAM_INICIO, inicio);
  proximo.set(PARAM_FIM, fim);
  if (valor.preset && valor.preset !== 'custom') proximo.set(PARAM_ATALHO, valor.preset);
  return proximo;
}

export function paraParametrosApi(valor: PeriodoValor): NegociacaoPeriodoParams {
  const intervalo = intervaloOrdenado(valor);
  if (!intervalo) return {};
  const [periodoInicio, periodoFim] = intervalo;
  return {
    periodoCampo: ehCampo(valor.campo) ? valor.campo : CAMPO_PADRAO,
    periodoInicio,
    periodoFim,
  };
}

export const campoDoPeriodoAtivo = (valor: PeriodoValor): NegociacaoPeriodoCampo | null =>
  periodoEstaAtivo(valor) && ehCampo(valor.campo) ? valor.campo : null;

const opcaoDoCampo = (campo: string) =>
  periodoCampoOptions.find((opcao) => opcao.key === campo) ?? periodoCampoOptions[0];

const nomeDoCampo = (campo: string): string => opcaoDoCampo(campo).long;

export const rotuloDoChipPeriodo = (valor: PeriodoValor): string =>
  `${nomeDoCampo(valor.campo)}: ${rotuloDoPreset(valor.preset) ?? rotuloDoIntervalo(valor)}`;

export function legendaDoPeriodo(valor: PeriodoValor): string {
  const intervalo = intervaloOrdenado(valor);
  if (!intervalo) return '';
  const [inicio, fim] = intervalo;
  if (inicio === fim) return `${nomeDoCampo(valor.campo)} em ${isoParaBr(inicio)}`;
  return `${nomeDoCampo(valor.campo)} entre ${isoParaBr(inicio)} e ${isoParaBr(fim)}`;
}

export const rotuloDaColunaDeData = (campo: NegociacaoPeriodoCampo | null): string =>
  opcaoDoCampo(campo ?? CAMPO_PADRAO).coluna;

export function valorDaColunaDeData(
  row: NegociacaoListRow,
  campo: NegociacaoPeriodoCampo | null,
): string {
  if (campo === 'ultimaInteracao') return row.ultimaInteracaoEm || TRACO;
  if (campo === 'acordo') return row.dataAcordo || TRACO;
  return row.createdAt;
}

export function resumoDaPrevia(total: number | null, carregando: boolean): string | undefined {
  if (carregando) return 'Calculando resultados…';
  if (total === null) return undefined;
  return `${total.toLocaleString('pt-BR')} ${flexionar(total, 'negociação', 'negociações')} neste período`;
}
